import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    api
      .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setStatus("success");
        setMessage(r.data.message || "Your email is verified.");
        setTimeout(() => navigate("/login", { replace: true, state: { email: r.data.email } }), 2500);
      })
      .catch((err) => {
        setStatus("error");
        const d = err.response?.data?.detail;
        setMessage(typeof d === "string" ? d : "This verification link is invalid or has expired.");
      });
  }, [token, navigate]);

  return (
    <AuthShell>
      <div className="text-center" data-testid="verify-email-page">
        {status === "verifying" && (
          <div data-testid="verify-verifying">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Verifying your email…</h2>
            <p className="mt-2 text-sm text-muted-foreground">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Email verified</h2>
            <p className="mt-2 text-sm text-muted-foreground">{message} Redirecting you to sign in…</p>
            <Button className="mt-6" onClick={() => navigate("/login", { replace: true })} data-testid="verify-to-login">
              Go to login
            </Button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Verification failed</h2>
            <p className="mt-2 text-sm text-muted-foreground">{message}</p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <Link to="/login" className="font-semibold text-primary underline-offset-4 hover:underline" data-testid="verify-error-login">
                Go to login to request a new link
              </Link>
            </div>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
